// Marchés clients : situation des bons de commande (SENELEC…), factures à encaisser, programmes de livraison (ADL).
// Remplace les onglets RECAP FACTURATION, SITUATION et les fichiers de suivi des ADL.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, GlobalStyles, Grid, IconButton, InputAdornment, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined, AddOutlined, AssignmentOutlined, CloudDownloadOutlined, LocalShippingOutlined,
  PrintOutlined, ReceiptLongOutlined, ReportProblemOutlined, WarningAmberOutlined, SearchOutlined, CloseOutlined, PaymentsOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { marcheAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import { AdlDialog, Avancement, MARCHE_STATUT } from './common';
import { ReglementDialog, Reglements } from './Reglement';

const M = ({ v, color, bold }) => (
  <Box component="span" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', color, fontWeight: bold ? 700 : undefined }}>{fcfa(v)}</Box>
);
const TAB_KEY = 'marches.onglet';
// Recherche sans tenir compte des accents ni de la casse
const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const contient = (q, ...vals) => vals.some((v) => norm(v).includes(q));

const Marches = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [d, setD] = useState(null);
  const [adls, setAdls] = useState(null);
  const [tab, setTab] = useState(() => { try { return localStorage.getItem(TAB_KEY) || 'bc'; } catch { return 'bc'; } });
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [adlDlg, setAdlDlg] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [livr, setLivr] = useState([]);
  const [reglDlg, setReglDlg] = useState(false);
  const [reglKey, setReglKey] = useState(0);

  const load = useCallback(() => {
    marcheAPI.list().then((r) => setD(r.data)).catch((e) => setError(apiError(e)));
    marcheAPI.adls().then((r) => setAdls(r.data.adls)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  // Livraisons correspondant à la recherche (n° de BL, destination, transporteur, véhicule, chauffeur)
  useEffect(() => {
    if (q.trim().length < 2) { setLivr([]); return undefined; }
    const t = setTimeout(() => marcheAPI.recherche(q.trim()).then((r) => setLivr(r.data.livraisons)).catch(() => setLivr([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  const nq = norm(q.trim());
  const marchesF = useMemo(() => (d?.marches || []).filter((m) => !nq || contient(nq, m.numero, m.rang && `n°${m.rang}`, m.rang && `bc ${m.rang}`, dateFr(m.date_bc), m.client_nom, m.objet)), [d, nq]);
  const impayesF = useMemo(() => (d?.impayes || []).filter((f) => !nq || contient(nq, f.numero, f.marche_numero, f.marche_rang && `n°${f.marche_rang}`, dateFr(f.date_enlevement), dateFr(f.echeance))), [d, nq]);
  const adlsF = useMemo(() => (adls || []).filter((a) => !nq || contient(nq, a.libelle, a.periode, a.numero)), [adls, nq]);
  const choisir = (v) => { setTab(v); try { localStorage.setItem(TAB_KEY, v); } catch { /* sans mémoire */ } };

  const reprise = async () => {
    if (!window.confirm("Importer l'historique SENELEC (BC n°1 à 8, factures à encaisser, ADL 2026) tel qu'il figure dans les fichiers Excel ?")) return;
    setBusy(true);
    try { const r = await marcheAPI.reprise(); setInfo(r.data.message); load(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  const t = d?.totaux;
  const impayes = impayesF;
  return (
    <Layout title="Marchés clients" plain
      actions={(
        <>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
          {d?.ecriture && <Button variant="outlined" startIcon={<AddOutlined />} onClick={() => setAdlDlg(true)}>Nouvel ADL</Button>}
          {d?.ecriture && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => navigate('/marches/nouveau')}>Nouveau BC</Button>}
        </>
      )}>
      <GlobalStyles styles={{ '@media print': { 'nav, aside, .MuiDrawer-root, .no-print, button, .MuiTabs-root': { display: 'none !important' }, body: { background: '#fff !important' } } }} />
      {info && <Alert severity="success" sx={{ mb: 2 }} className="no-print" onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} className="no-print" onClose={() => setError('')}>{error}</Alert>}
      {d && !d.marches.length && (
        <Alert severity="info" sx={{ mb: 2 }} action={d.reprise_possible && (
          <Button color="inherit" startIcon={<CloudDownloadOutlined />} disabled={busy} onClick={reprise}>Importer l'historique</Button>
        )}>
          Aucun bon de commande client. {d.reprise_possible ? "Importez l'historique SENELEC (BC n°1 à 8 et ADL 2026) ou saisissez un nouveau BC." : 'Saisissez un nouveau BC.'}
        </Alert>
      )}

      {t && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={2.4}><Kpi compact icon={AssignmentOutlined} tint="navy" label="Commandé" value={fcfa(t.commande_ht)} valueSize={20} sub="F HT, BC en cours et soldés" /></Grid>
          <Grid item xs={6} md={2.4}><Kpi compact icon={ReceiptLongOutlined} tint="gray" label="Facturé" value={fcfa(t.facture_ht)} valueSize={20} sub="F HT" /></Grid>
          <Grid item xs={6} md={2.4}><Kpi compact icon={AccountBalanceWalletOutlined} tint="green" label="Payé" value={fcfa(t.paye_ht)} valueSize={20} sub="F HT" /></Grid>
          <Grid item xs={6} md={2.4}>
            <Kpi compact icon={WarningAmberOutlined} tint="red" label="Non payé" value={fcfa(t.non_paye_ht)} valueSize={20} valueColor={t.non_paye_ht > 0 ? '#B4232A' : undefined}
              sub={`dont échu ${fcfa(t.echu_ht)} F`} onClick={() => choisir('impayes')} />
          </Grid>
          <Grid item xs={12} md={2.4}><Kpi compact icon={LocalShippingOutlined} tint="amber" label="Reste à livrer" value={fcfa(t.reste_a_livrer_ht)} valueSize={20} sub="F HT" /></Grid>
        </Grid>
      )}

      {d && d.marches.some((m) => !m.lignes?.length && m.statut === 'OUVERT') && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {d.marches.filter((m) => !m.lignes?.length && m.statut === 'OUVERT').length} BC déposé(s) sans saisie, à compléter (types, quantités, prix) : repérez le badge « À compléter » ci-dessous.
        </Alert>
      )}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid', borderColor: 'divider', pr: 1.5 }}>
          <Tabs value={tab} onChange={(e, v) => choisir(v)} variant="scrollable" sx={{ px: 1, flex: '1 1 auto', minWidth: 0 }}>
            <Tab value="bc" label={`Bons de commande${d ? ` (${marchesF.length})` : ''}`} />
            <Tab value="impayes" label={`À encaisser${impayes.length ? ` (${impayes.length})` : ''}`} />
            <Tab value="adl" label={`Programmes de livraison${adls ? ` (${adlsF.length})` : ''}`} />
            <Tab value="reglements" label="Règlements" />
          </Tabs>
          <TextField className="no-print" size="small" value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° BC, facture, BL, ADL, destination…"
            inputProps={{ 'aria-label': 'Rechercher dans les marchés' }} sx={{ my: 1, ml: 1, width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment>,
              endAdornment: q ? <InputAdornment position="end"><IconButton size="small" aria-label="Effacer la recherche" onClick={() => setQ('')}><CloseOutlined fontSize="small" /></IconButton></InputAdornment> : null,
            }} />
        </Box>
        {nq && livr.length > 0 && (
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#F7F8FA' }}>
            <Typography sx={{ px: 2, pt: 1.25, fontSize: 13, fontWeight: 600 }}>Livraisons trouvées ({livr.length})</Typography>
            <TableContainer sx={{ overflowX: 'auto', maxHeight: 280 }}>
              <Table size="small" stickyHeader aria-label="Livraisons trouvées">
                <TableHead><TableRow>
                  <TableCell>BL</TableCell><TableCell>Date</TableCell><TableCell>Destination</TableCell><TableCell>Type</TableCell><TableCell align="right">Qté</TableCell>
                  <TableCell>Transporteur · véhicule</TableCell><TableCell>BC · ADL</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {livr.map((l) => (
                    <TableRow key={`${l.source}-${l.delivery_id || l.id}`} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(l.source === 'ERP' ? `/bons-livraison/${l.delivery_id}` : `/marches/adl/${l.adl_id}`)}>
                      <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{l.bl || '—'}{l.source === 'REPRISE' && <Chip size="small" label="reprise" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(l.date) || '—'}</TableCell>
                      <TableCell>{l.destination || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.pba_type || (l.source === 'ERP' ? 'voir BL' : '—')}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{l.quantite}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{[l.transporteur, l.vehicule].filter(Boolean).join(' · ') || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{[l.marche_numero && `BC ${l.marche_rang ? `n°${l.marche_rang} ` : ''}${l.marche_numero}`, l.adl_libelle].filter(Boolean).join(' · ') || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 'bc' && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>N°</TableCell><TableCell>Bon de commande</TableCell><TableCell align="right">Montant total</TableCell>
                  <TableCell align="right">Facturé</TableCell><TableCell align="right">Payé</TableCell><TableCell align="right">Non payé</TableCell>
                  <TableCell align="right">Reste à livrer</TableCell><TableCell>Livré</TableCell><TableCell>État</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {marchesF.map((m) => (
                  <TableRow key={m.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/marches/${m.id}`)}>
                    <TableCell sx={{ fontWeight: 700 }}>{m.rang || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontFamily: tokens.mono, fontWeight: 600, fontSize: 14 }}>{m.numero}{!m.lignes?.length && <Chip size="small" color="warning" label="À compléter" sx={{ ml: 1, height: 20, fontSize: 11, fontFamily: 'inherit' }} />}</Typography>
                        {m.alertes.length > 0 && <Tooltip title={m.alertes.join(' · ')}><ReportProblemOutlined sx={{ fontSize: 17, color: '#B7791F' }} /></Tooltip>}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>du {dateFr(m.date_bc)}{/senelec/i.test(m.client_nom) && m.client_nom.length < 10 ? '' : ` · ${m.client_nom}`}</Typography>
                    </TableCell>
                    <TableCell align="right"><M v={m.totaux.commande_ht} /></TableCell>
                    <TableCell align="right"><M v={m.totaux.facture_ht} /></TableCell>
                    <TableCell align="right"><M v={m.totaux.paye_ht} /></TableCell>
                    <TableCell align="right"><M v={m.totaux.non_paye_ht} color={m.totaux.non_paye_ht > 0 ? (m.totaux.echu_ht > 0 ? '#B4232A' : undefined) : (m.totaux.non_paye_ht < 0 ? '#8A5A0B' : tokens.steel)} bold={m.totaux.echu_ht > 0} /></TableCell>
                    <TableCell align="right"><M v={m.totaux.reste_a_livrer_ht} color={m.totaux.reste_a_livrer_ht ? undefined : tokens.steel} /></TableCell>
                    <TableCell><Avancement value={m.totaux.avancement} width={96} /></TableCell>
                    <TableCell><Chip size="small" label={MARCHE_STATUT[m.statut].label} color={MARCHE_STATUT[m.statut].color} variant="outlined" /></TableCell>
                  </TableRow>
                ))}
                {nq && !marchesF.length && <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun bon de commande ne correspond à la recherche{livr.length ? ' (voir les livraisons trouvées ci-dessus)' : ''}.</TableCell></TableRow>}
                {t && !nq && d.marches.length > 0 && (
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                    <TableCell /><TableCell>Total</TableCell>
                    <TableCell align="right"><M v={t.commande_ht} bold /></TableCell><TableCell align="right"><M v={t.facture_ht} bold /></TableCell>
                    <TableCell align="right"><M v={t.paye_ht} bold /></TableCell><TableCell align="right"><M v={t.non_paye_ht} bold /></TableCell>
                    <TableCell align="right"><M v={t.reste_a_livrer_ht} bold /></TableCell><TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {t && !nq && t.livre_non_facture_ht > 0 && (
              <Alert severity="warning" sx={{ m: 1.5 }}>Livraisons non encore facturées : <b>{fcfa(t.livre_non_facture_ht)} F HT</b>.</Alert>
            )}
          </TableContainer>
        )}

        {tab === 'impayes' && d?.ecriture && (d?.impayes || []).length > 0 && (
          <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', flex: '1 1 260px' }}>
              Un virement reçu ? Enregistrez-le une fois et cochez les factures qu'il règle : la situation se met à jour.
            </Typography>
            <Button variant="contained" color="success" startIcon={<PaymentsOutlined />} onClick={() => setReglDlg(true)}>Enregistrer un règlement</Button>
          </Box>
        )}
        {tab === 'reglements' && <Reglements key={reglKey} ecriture={d?.ecriture} navigate={navigate} onChange={load} />}
        {tab === 'impayes' && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Facture</TableCell><TableCell>BC</TableCell><TableCell>Enlèvement</TableCell><TableCell>Valid. DAPA</TableCell>
                  <TableCell>Valid. compta.</TableCell><TableCell>Échéance</TableCell><TableCell align="right">HT</TableCell>
                  <TableCell align="right">TTC</TableCell><TableCell>Situation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nq && !impayes.length && <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune facture ne correspond à la recherche.</TableCell></TableRow>}
                {impayes.map((f) => (
                  <TableRow key={`${f.source}-${f.id}`} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/marches/${f.marche_id}`)}>
                    <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {f.numero}{f.source === 'REPRISE' && <Chip size="small" label="reprise" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                    </TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, fontSize: 13, whiteSpace: 'nowrap' }}>{f.marche_rang ? `n°${f.marche_rang} · ` : ''}{f.marche_numero}</TableCell>
                    <TableCell>{dateFr(f.date_enlevement)}</TableCell>
                    <TableCell>{dateFr(f.validation_dapa) || <span style={{ color: '#8A5A0B' }}>à obtenir</span>}</TableCell>
                    <TableCell>{dateFr(f.validation_compta) || <span style={{ color: '#8A5A0B' }}>à obtenir</span>}</TableCell>
                    <TableCell>{dateFr(f.echeance) || '—'}</TableCell>
                    <TableCell align="right"><M v={f.reste_ht} /></TableCell>
                    <TableCell align="right"><M v={f.reste_ttc} /></TableCell>
                    <TableCell>
                      {f.type === 'AVOIR' ? <Chip size="small" label="Avoir" color="warning" variant="outlined" />
                        : f.type === 'ECART' ? <Chip size="small" label="Écart à justifier" color="warning" />
                          : f.echu ? <Chip size="small" label="Échu" color="error" /> : <Chip size="small" label="Non échu" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
                {t && !nq && (
                  <>
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                      <TableCell colSpan={6}>Montant à payer</TableCell>
                      <TableCell align="right"><M v={t.non_paye_ht} bold /></TableCell><TableCell align="right"><M v={t.non_paye_ttc} bold /></TableCell><TableCell />
                    </TableRow>
                    <TableRow><TableCell colSpan={6} sx={{ color: '#B4232A', fontWeight: 600 }}>dont échu</TableCell><TableCell align="right"><M v={t.echu_ht} color="#B4232A" bold /></TableCell><TableCell colSpan={2} /></TableRow>
                    <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>dont non échu, avoirs et écarts</TableCell><TableCell align="right"><M v={t.non_paye_ht - t.echu_ht} /></TableCell><TableCell colSpan={2} /></TableRow>
                  </>
                )}
              </TableBody>
            </Table>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', p: 1.5 }}>
              Échéance = validation par la comptabilité du client + délai de paiement du BC (30 jours par défaut). Sans validation, l'échéance de la facture s'applique.
            </Typography>
          </TableContainer>
        )}

        {tab === 'adl' && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ADL</TableCell><TableCell>Période</TableCell><TableCell align="right">À livrer</TableCell><TableCell align="right">Livré</TableCell>
                  <TableCell align="right">Reste</TableCell><TableCell>Avancement</TableCell><TableCell>État</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adlsF.map((a) => (
                  <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/marches/adl/${a.id}`)}>
                    <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{a.libelle}</TableCell>
                    <TableCell>{a.periode || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{a.totaux.a_livrer}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{a.totaux.livre}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: a.totaux.reste ? '#B4232A' : tokens.steel }}>{a.totaux.reste}</TableCell>
                    <TableCell><Avancement value={a.totaux.avancement} /></TableCell>
                    <TableCell><Chip size="small" label={a.statut === 'CLOS' ? 'Clos' : 'En cours'} color={a.statut === 'CLOS' ? 'default' : 'info'} variant="outlined" /></TableCell>
                  </TableRow>
                ))}
                {adls && !adlsF.length && <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>{nq ? 'Aucun programme ne correspond à la recherche.' : 'Aucun programme de livraison.'}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Panel>
      <ReglementDialog open={reglDlg} onClose={() => setReglDlg(false)} impayes={d?.impayes || []}
        onSaved={(r) => { setReglDlg(false); setInfo(r.message); setReglKey((k) => k + 1); load(); }} />
      <AdlDialog open={adlDlg} onClose={() => setAdlDlg(false)} onSaved={(r) => { setAdlDlg(false); navigate(`/marches/adl/${r.id}`, { state: { info: r.message } }); }} />
    </Layout>
  );
};

export default Marches;
